import type { MedianIncomeYear } from '../constants';
import { GLOSSARY,MEDIAN_INCOME_BY_YEAR } from '../constants';

interface ConceptGridProps {
  year: MedianIncomeYear;
}

/**
 * ⑬ 중위소득 한눈에 알아두기 — 산정/활용/OECD/차상위 4개 콘셉트 카드 + 용어집.
 * - 3번째 카드(OECD)에는 분류 미니 테이블을 함께 노출.
 * - 용어집(GLOSSARY)은 연도 무관 공용.
 */
export const ConceptGrid = ({ year }: ConceptGridProps) => {
  const { conceptCards } = MEDIAN_INCOME_BY_YEAR[year];

  return (
    <section className="flex flex-col gap-16">
      <h2 className="font-700 text-Gray-900 typo-h5 mobile-tablet:px-16 mobile-tablet:typo-h6">
        중위소득, 한눈에 알아두기
      </h2>
      <p className="text-Gray-700 typo-body4 mobile-tablet:px-16">
        중위소득은{' '}
        <strong className="font-700 text-Gray-900">경상소득의 중간값</strong>을
        가구규모에 맞춰 환산한 값으로, 우리나라 복지정책의 핵심 기준선입니다.
      </p>

      <div className="flex flex-col gap-12">
        {conceptCards.map((c, idx) => (
          <article
            key={c.title}
            className="flex flex-col gap-8 rounded-small border-1 border-Gray-100 bg-Gray-25 p-16 mobile-tablet:rounded-none mobile-tablet:border-0 mobile-tablet:border-b-1 mobile-tablet:border-Gray-100 mobile-tablet:bg-transparent mobile-tablet:px-16 mobile-tablet:py-12"
          >
            <div className="font-700 text-Gray-900 typo-body3">{c.title}</div>
            <p
              className="text-Gray-700 typo-body4"
              dangerouslySetInnerHTML={{ __html: c.body }}
            />
            {/* OECD 카드(index 2)에는 분류 미니 테이블 */}
            {idx === 2 ? (
              <table className="w-full border-collapse rounded-xsmall border-1 border-Gray-200 text-Gray-700 typo-caption1">
                <thead>
                  <tr className="bg-Gray-50">
                    <th className="border-b-1 border-Gray-200 px-12 py-8 text-left font-600">
                      구간
                    </th>
                    <th className="border-b-1 border-Gray-200 px-12 py-8 text-left font-600">
                      분류
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-t-1 border-Gray-100 px-12 py-8">
                      중위소득 &lt; 50%
                    </td>
                    <td className="border-t-1 border-Gray-100 px-12 py-8">
                      빈곤층
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t-1 border-Gray-100 px-12 py-8">
                      50% ~ 150%
                    </td>
                    <td className="border-t-1 border-Gray-100 px-12 py-8">
                      중산층
                    </td>
                  </tr>
                  <tr>
                    <td className="border-t-1 border-Gray-100 px-12 py-8">
                      &gt; 150%
                    </td>
                    <td className="border-t-1 border-Gray-100 px-12 py-8">
                      상류층
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}
            {c.footnote ? (
              <p className="text-Gray-600 typo-caption1">{c.footnote}</p>
            ) : null}
          </article>
        ))}
      </div>

      <div className="rounded-small bg-Gray-50 p-16 mobile-tablet:rounded-none">
        <div className="mb-8 font-700 text-Gray-900 typo-body3">
          함께 알아둘 개념
        </div>
        <dl className="flex flex-col gap-8">
          {GLOSSARY.map((g) => (
            <div key={g.term} className="flex flex-col gap-2">
              <dt className="font-600 text-Gray-900 typo-body4">{g.term}</dt>
              <dd
                className="text-Gray-700 typo-caption1"
                dangerouslySetInnerHTML={{ __html: g.def }}
              />
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};
